import { styledButtonStyles } from '@components/AlternativesDropdown/AlternativesDropdown.styles';
import {
  Box,
  Button,
  freddyTheme,
  Modal,
  ModalBody,
  Typography
} from '@coolblue-development/freddy';
import productTypeOpenUpsellDropdownTrigger from '@coolblue-development/ga4-events/triggers/ProductTypeSection/openUpsellDropdownTrigger';
import productTypeSelectUpsellAlternativeTrigger from '@coolblue-development/ga4-events/triggers/ProductTypeSection/selectUpsellAlternativeTrigger';
import productTypeShowCollectionTrigger from '@coolblue-development/ga4-events/triggers/ProductTypeSection/showCollectionTrigger';
import automatedOpenUpsellDropdownTrigger from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/openUpsellDropdownTrigger';
import automatedSelectUpsellAlternativeTrigger from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/selectUpsellAlternativeTrigger';
import automatedShowCollectionTrigger from '@coolblue-development/ga4-events/triggers/RecommendedAccessories/showCollectionTrigger';
import { ArrowRight } from '@coolblue-development/icons';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import {
  CollectionDropdown,
  CollectionDropdownOption
} from '@coolblue-development/next-patterns/client';
import { useAccessoryProductCardContext } from '@features/add-to-cart-page/components/Sections/AccessoryProductCard/AccessoryProductCard.context';
import {
  HighlightedAlternative,
  HighlightedAlternativeSlot,
  Product
} from '@features/add-to-cart-page/typings/Product';
import { useBreakpoints } from '@hooks/useBreakpoints';
import useTestId from '@hooks/useTestId';
import { useTranslation } from '@i18n/useTranslation';
import React, { ReactElement, useEffect, useState } from 'react';

export default function AlternativesDropdown({
  highlightedAlternative,
  product,
  isAutomated,
  accessoryProductCardIndex,
  accessoryProductCardTotalIndices
}: {
  highlightedAlternative: HighlightedAlternative;
  product: Product;
  isAutomated: boolean;
  accessoryProductCardIndex: number;
  accessoryProductCardTotalIndices: number;
}): ReactElement {
  const { t } = useTranslation('webshop');
  const productTypeName = product.productType?.singularName;
  const ariaLabel = t('alternativesDropdown.chooseAlternative', { productTypeName });
  const modalTranslations = useFreddyTranslations('modal');
  const testId = useTestId('product-alternatives-dropdown');
  const { isMobile } = useBreakpoints();
  const context = useAccessoryProductCardContext();
  const [isOpen, setIsOpen] = useState(false);
  const initiallySelectedIndex = highlightedAlternative.slots
    .findIndex(slot => {
      return slot.products[0].product?.id === product.id;
    })
    .toString();

  const [selectedIndex, setSelectedIndex] = useState(initiallySelectedIndex);
  const { trackEvent } = useAnalytics();

  const slotTitle = context.productAlternatives[context.selectedProductAlternative].slotTitle;

  useEffect(() => {
    context.updateProductAlternatives({ highlightedAlternative });

    if (isAutomated) {
      trackEvent(automatedShowCollectionTrigger({ sub_feature: 'upsell_collection' }));
    } else {
      trackEvent(productTypeShowCollectionTrigger({ sub_feature: 'upsell_collection' }));
    }
    // A single run is required when the component is rendered, hence the empty array for dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const index = highlightedAlternative.slots.findIndex(slot => {
      return slot.products[0].product?.id === context.selectedProductAlternative;
    });
    if (index > -1) {
      setSelectedIndex(index.toString());
    }
  }, [context, context.selectedProductAlternative, highlightedAlternative.slots]);

  const onAlternativeSelected = (value: string): void => {
    setSelectedIndex(value);

    const selectedProductId = highlightedAlternative.slots[+value].products[0].product?.id;

    if (!selectedProductId) {
      return;
    }

    context.updateSelectedColorAlternatives(selectedProductId, 0);
    context.setSelectedProductAlternative(selectedProductId);

    const params = {
      index: accessoryProductCardIndex,
      item_id: selectedProductId,
      total_indices: accessoryProductCardTotalIndices
    };
    trackEvent(
      isAutomated
        ? automatedSelectUpsellAlternativeTrigger(params)
        : productTypeSelectUpsellAlternativeTrigger(params)
    );
  };

  const trackOpen = () => {
    const numberOfItems = highlightedAlternative.slots.length;
    const params = {
      index: accessoryProductCardIndex,
      number_of_items: numberOfItems,
      total_indices: accessoryProductCardTotalIndices
    };
    trackEvent(
      isAutomated
        ? automatedOpenUpsellDropdownTrigger(params)
        : productTypeOpenUpsellDropdownTrigger(params)
    );
  };
  const CollectionDropdownContent = (
    <CollectionDropdown
      name={`collection-dropdown-${product.id}`}
      initialTitle={t('alternativesDropdown.chooseAnOption')}
      ariaLabel={ariaLabel}
      initialValue={selectedIndex}
      onChange={value => {
        onAlternativeSelected(value);
      }}
      variant={isMobile ? 'contentOnly' : 'default'}
      title={slotTitle}
      value={selectedIndex}
      onOpen={() => trackOpen()}
    >
      {highlightedAlternative.slots.map(
        (slot: HighlightedAlternativeSlot, index: number): ReactElement => {
          if (!slot.products[0].product?.primaryImage?.id) return <></>;

          return (
            <CollectionDropdownOption
              key={slot.products[0].product.id}
              imageId={slot.products[0].product.primaryImage.id}
              title={slot.title}
              value={index.toString()}
              description={slot.products[0].description}
            />
          );
        }
      )}
    </CollectionDropdown>
  );

  return (
    <Box {...testId}>
      <Typography mb={100} fontWeight={freddyTheme.typography.fontWeight.openSans.semiBold}>
        {t('alternativesDropdown.variant')}
      </Typography>
      {isMobile ? (
        <>
          <Button
            endIcon={<ArrowRight />}
            variant='secondary'
            sx={styledButtonStyles}
            onClick={() => {
              setIsOpen(!isOpen);
              if (!isOpen) {
                trackOpen();
              }
            }}
          >
            <Typography truncateLines={1} m={0}>
              {slotTitle || t('alternativesDropdown.chooseAnOption')}
            </Typography>
          </Button>
          <Modal
            onClose={() => {
              setIsOpen(!isOpen);
            }}
            title={t('alternativesDropdown.variant')}
            aria-label={ariaLabel}
            open={isOpen}
            body={<ModalBody>{CollectionDropdownContent}</ModalBody>}
            placement='bottom'
            size='large'
            translations={modalTranslations}
          />
        </>
      ) : (
        CollectionDropdownContent
      )}
    </Box>
  );
}
